import { createBrowserRouter, Navigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import React, { Suspense, useContext, useEffect, useState } from "react";
import MainContext from "src/context/MainContext";
import SignIn from "../auth/login";
import { PrivacyPage, TermsPage } from "../pages/Legal";
import Logout from "src/pages/Logout";
import ChunkErrorBoundary from "src/components/ChunkErrorBoundary";
import ClubDisabledPopup from "../components/ClubDisabledPopup";
import { isCustomerMessagesEnabledForCurrentClub } from "src/utils/customerMessagesAccess";
import { isPoloClub, isPoloOnlyClub, isPathAllowedForPoloOnly } from "src/utils/poloAccess";
import { isLodgingClub } from "src/utils/lodgingAccess";
import { isTripsClub } from "src/utils/verticalAccess";

import Loading from "src/constants/Loading";
import { lazyWithChunkReload } from "src/utils/reloadOnChunkError";
// Eager: a lazy errorElement suspends (or misses its chunk) while
// RenderErrorBoundary is already handling a route error. Do not code-split
// this page — GZ#2753 / #2627, React Router "do not lazy-load errorElement".
import ErrorElement from "../pages/ErrorElement";
// Tiny tab bar; loaded eagerly so it needs no Suspense of its own.
import IntelligenceLayout from "src/features/Intelligence/IntelligenceLayout";
const SuperAdminSettings = lazyWithChunkReload(
  () => import("../pages/SuperAdminSettings")
);
const QuickBooksCallback = lazyWithChunkReload(
  () => import("../pages/QuickBooksCallback")
);
const LocationManager = lazyWithChunkReload(
  () => import("src/features/Admin/LocationManager/LocationManager")
);
const MainPage = lazyWithChunkReload(() => import("../pages/MainPage"));
const CalenderPage = lazyWithChunkReload(() => import("../pages/CalenderPage"));
const Services = lazyWithChunkReload(() => import("../pages/Services"));
const CourtsSetting = lazyWithChunkReload(() => import("../pages/CourtsSettings"));
const Payments = lazyWithChunkReload(() => import("src/pages/Payments"));
const CoachFeedback = lazyWithChunkReload(() => import("src/pages/CoachFeedback"));
const AppFeedback = lazyWithChunkReload(() => import("src/pages/AppFeedback"));
const Recordings = lazyWithChunkReload(() => import("src/pages/Recordings"));
const Programs = lazyWithChunkReload(() => import("src/pages/Programs"));
const ServiceFeedback = lazyWithChunkReload(() => import("src/pages/ServiceFeedback"));
const Polls = lazyWithChunkReload(() => import("src/pages/Polls"));
// Screens & Live Score — TVs at the venue and matches scored live from a phone.
const Screens = lazyWithChunkReload(() => import("src/pages/Screens"));
const ScreenEditor = lazyWithChunkReload(() => import("src/pages/ScreenEditor"));
// Forms — public sign-up pages (events, memberships, gift cards) and their builder.
const Forms = lazyWithChunkReload(() => import("src/pages/Forms"));
const FormEditor = lazyWithChunkReload(() => import("src/pages/FormEditor"));
// Public form pages (no login): the form, the club's list, the result page.
// Own chunks; they import no admin shell (sidebar, socket, admin API).
const PublicFormPage = lazyWithChunkReload(() => import("src/pages/forms/PublicFormPage"));
const PublicClubPage = lazyWithChunkReload(() => import("src/pages/forms/PublicClubPage"));
const PublicResultPage = lazyWithChunkReload(() => import("src/pages/forms/PublicResultPage"));
const Customers = lazyWithChunkReload(() => import("src/pages/Customers"));
const SuperAccounts = lazyWithChunkReload(() => import("src/pages/SuperAccounts"));
const Statistics = lazyWithChunkReload(() => import("src/pages/Statistics"));
const AppAnalyticsPage = lazyWithChunkReload(() => import("src/pages/AppAnalyticsPage"));
const McpConnectionPage = lazyWithChunkReload(() => import("src/pages/McpConnection"));
const StatsAssistantPage = lazyWithChunkReload(() => import("src/pages/StatsAssistantPage"));
const Accounting = lazyWithChunkReload(() => import("src/pages/Accounting"));
const POS = lazyWithChunkReload(() => import("src/pages/POS"));
const Automations = lazyWithChunkReload(() => import("src/pages/Automations"));
const PushNotifications = lazyWithChunkReload(() => import("src/pages/PushNotifications"));
const UnpaidBalanceReminders = lazyWithChunkReload(() => import("src/pages/UnpaidBalanceReminders"));
const Referrals = lazyWithChunkReload(() => import("src/pages/Referrals"));
const ReferralProgramDetail = lazyWithChunkReload(
  () => import("src/features/Referrals/ProgramDetail")
);
const Loyalty = lazyWithChunkReload(() => import("src/pages/Loyalty"));
const CustomerMessages = lazyWithChunkReload(() => import("src/pages/CustomerMessages"));
const Support = lazyWithChunkReload(() => import("src/pages/Support"));
const SupportHub = lazyWithChunkReload(() => import("src/features/SupportHub"));
const PublicPayment = lazyWithChunkReload(() => import("src/pages/PublicPayment"));
const PublicMembershipPayment = lazyWithChunkReload(() => import("src/pages/PublicMembershipPayment"));
const PublicVoucherPayment = lazyWithChunkReload(() => import("src/pages/PublicVoucherPayment"));
const CommunityInviteJoin = lazyWithChunkReload(() => import("src/pages/CommunityInviteJoin"));
// GZ#3693 — public guest sign-up form (waiver + desk QR), no login.
const GuestSignup = lazyWithChunkReload(() => import("src/pages/GuestSignup"));
const CheckIn = lazyWithChunkReload(() => import("src/pages/CheckIn"));
const PaymentSuccess = lazyWithChunkReload(() => import("src/pages/PaymentSuccess"));
const BookingAgentPage = lazyWithChunkReload(() => import("src/pages/BookingAgentPage"));
const PublicChatPage = lazyWithChunkReload(() => import("src/pages/PublicChatPage"));
// Self-serve check-in kiosk (GZ#2469 round 4) — public, token-keyed, no admin chrome.
const Kiosk = lazyWithChunkReload(() => import("src/pages/Kiosk"));
// Screens & Live Score public surfaces — a TV (token in URL, no session), the
// phone remote for one match (control token), and the staff court desk.
const ScreenTV = lazyWithChunkReload(() => import("src/pages/screens/ScreenTV"));
const ScreenRemote = lazyWithChunkReload(() => import("src/pages/screens/ScreenRemote"));
const ScreenOverlay = lazyWithChunkReload(() => import("src/pages/screens/ScreenOverlay"));
const CourtDesk = lazyWithChunkReload(() => import("src/pages/screens/CourtDesk"));
// Americano / Mexicano club screen (any TV or iPad) and the court-QR score page — public, no login.
const AmericanoScreen = lazyWithChunkReload(() => import("src/pages/screens/AmericanoScreen"));
const AmericanoScore = lazyWithChunkReload(() => import("src/pages/screens/AmericanoScore"));
const Changelog = lazyWithChunkReload(() => import("src/pages/Changelog"));
const Trips = lazyWithChunkReload(() => import("src/pages/Trips"));
// Tournaments render full-page with their own topbar chrome — no admin sidebar
// (design directive) — so they mount as a top-level route, not a MainPage child.
const TournamentsApp = lazyWithChunkReload(() => import("src/features/Tournaments/design/TournamentsApp"));
const TripDetail = lazyWithChunkReload(() => import("src/pages/TripDetail"));
const TripCreate = lazyWithChunkReload(() => import("src/pages/TripCreate"));
const TripSite = lazyWithChunkReload(() => import("src/pages/TripSite"));
const TripCustomers = lazyWithChunkReload(() => import("src/pages/TripCustomers"));
const TripDocuments = lazyWithChunkReload(() => import("src/pages/TripDocuments"));
const TripTeam = lazyWithChunkReload(() => import("src/pages/TripTeam"));
const Restaurant = lazyWithChunkReload(() => import("src/pages/restaurant/Restaurant"));
const PoloLayout = lazyWithChunkReload(() => import("src/pages/PoloLayout"));
const LodgingLayout = lazyWithChunkReload(() => import("src/pages/LodgingLayout"));
const Verifications = lazyWithChunkReload(() => import("src/pages/Verifications"));
const BundleBuilderPage = lazyWithChunkReload(
  () => import("src/pages/BundleBuilderPage")
);

// B2B Partner Portal — served on its own domain (partners.bookandgo.app) but
// bundled in the same app. See `isPartnerHost` below.
const PartnerLogin = lazyWithChunkReload(() => import("src/pages/partner/PartnerLogin"));
const PartnerLayout = lazyWithChunkReload(() => import("src/pages/partner/PartnerLayout"));
const PartnerPaymentReturn = lazyWithChunkReload(() => import("src/pages/partner/PartnerPaymentReturn"));

// True when the app is served on the partner domain. Keeps partners out of the
// admin surface entirely — redirected, not merely hidden.
// bookandgo-partners.web.app is the Firebase site for hosting:partner
// (site id `partners` is reserved by another project). Custom domain
// partners.bookandgo.app still matches the startsWith check.
const isPartnerHost = () => {
  const host = window.location.hostname;
  return host.startsWith("partners.") || host === "bookandgo-partners.web.app";
};

const partnerPage = (node: React.ReactNode) => (
  <ChunkErrorBoundary>
    <Suspense fallback={<Loading />}>{node}</Suspense>
  </ChunkErrorBoundary>
);

const RequireAuth: React.FC<any> = ({ children }) => {
  // On the partner domain the admin app must never render — send partners to
  // their portal before any admin auth/token logic runs.
  if (isPartnerHost()) {
    return <Navigate to="/partner" replace />;
  }
  return <RequireAuthInner>{children}</RequireAuthInner>;
};

const RequireAuthInner: React.FC<any> = ({ children }) => {
  const location = useLocation();
  const mcpRequest = location.pathname === '/mcp-connection' ? new URLSearchParams(location.search).get('request') : null;
  const signInPath = mcpRequest && /^[A-Za-z0-9_-]{43}$/.test(mcpRequest) ? `/signin?mcp_request=${encodeURIComponent(mcpRequest)}` : '/signin';
  const context = useContext(MainContext);
  const authenticated = context?.authenticated;
  const [showDisabledPopup, setShowDisabledPopup] = useState(false);

  useEffect(() => {
    try {
      const token = localStorage.getItem("accessToken");
      if (token) {
        const decodedToken: any = jwtDecode(token);

        if (decodedToken.exp * 1000 < Date.now()) {
          localStorage.removeItem("accessToken");
          localStorage.removeItem("userData");
          localStorage.removeItem("user");
          window.location.href = signInPath;
          return;
        }

        // Check club status for non-super admins
        const userString = localStorage.getItem("user");
        if (userString) {
          const userData = JSON.parse(userString);
          const userRole = userData?.role?.name;
          const isEnabled = userData?.is_enabled;

          if (userRole !== "SUPER_ADMIN" && isEnabled === false) {
            setShowDisabledPopup(true);
          }
        }
      }
    } catch (error) {
      // Token is invalid or corrupted - clear and redirect to login
      console.error("Token validation error:", error);
      localStorage.removeItem("accessToken");
      localStorage.removeItem("userData");
      localStorage.removeItem("user");
      window.location.href = signInPath;
    }
  }, [context, signInPath]);

  if (!authenticated) {
    return <Navigate to={signInPath} />;
  }

  if (showDisabledPopup) {
    return <ClubDisabledPopup />;
  }

  return children;
};

const RequireAdminRole: React.FC<any> = ({ children }) => {
  try {
    const userString = localStorage.getItem("user");
    if (!userString) {
      // No user data - show LocationManager as fallback
      return (
        <ChunkErrorBoundary>
          <Suspense fallback={<Loading />}>
            <LocationManager />
          </Suspense>
        </ChunkErrorBoundary>
      );
    }

    const user_data = JSON.parse(userString);
    const userRole = user_data?.role?.name;

    if (userRole === "SUPER_ADMIN" || userRole === "CLUB_ADMIN") {
      return children;
    } else {
      return (
        <ChunkErrorBoundary>
          <Suspense fallback={<Loading />}>
            <LocationManager />
          </Suspense>
        </ChunkErrorBoundary>
      );
    }
  } catch (error) {
    // JSON parse failed or user data is corrupted
    console.error("RequireAdminRole error:", error);
    return (
      <ChunkErrorBoundary>
        <Suspense fallback={<Loading />}>
          <LocationManager />
        </Suspense>
      </ChunkErrorBoundary>
    );
  }
};

const RequireClubAdmin: React.FC<any> = ({ children }) => {
  try {
    const userString = localStorage.getItem("user");
    if (!userString) return <Navigate to="/signin" replace />;
    const user_data = JSON.parse(userString);
    const userRole = user_data?.role?.name;
    if (userRole === "SUPER_ADMIN" || userRole === "CLUB_ADMIN") {
      return children;
    }
    return <Navigate to="/stats" replace />;
  } catch {
    return <Navigate to="/signin" replace />;
  }
};

const RequireCustomerMessagesClub: React.FC<any> = ({ children }) => {
  if (isCustomerMessagesEnabledForCurrentClub()) return children;
  return <Navigate to="/push-notifications" replace />;
};

// Polo is a gated vertical (see src/utils/poloAccess.ts). Non-polo clubs that
// land on a /polo URL are bounced back to the calendar rather than shown a
// half-empty module.
const RequirePoloClub: React.FC<any> = ({ children }) => {
  if (isPoloClub()) return children;
  return <Navigate to="/calendars" replace />;
};

// Lodging is a gated vertical (see src/utils/lodgingAccess.ts). Non-lodging
// clubs that land on a /lodging URL are bounced back to the calendar rather
// than shown a half-empty module.
const RequireLodgingClub: React.FC<any> = ({ children }) => {
  if (isLodgingClub()) return children;
  return <Navigate to="/calendars" replace />;
};

// Trips is a gated vertical (see src/utils/verticalAccess.ts). Clubs without
// it that land on a /trips URL are bounced back to the calendar.
const RequireTripsClub: React.FC<any> = ({ children }) => {
  if (isTripsClub()) return children;
  return <Navigate to="/calendars" replace />;
};

// Polo-only clubs (PLYRS) have no Operations / Trips / Restaurant app: every
// admin route outside Polo and its allowed companions (/support) lands on /polo
// instead. One guard here covers the whole tree, so no ops route can be reached
// by URL once the switcher stops offering it.
const RequirePoloOnlyScope: React.FC<any> = ({ children }) => {
  const { pathname } = useLocation();
  if (isPoloOnlyClub() && !isPathAllowedForPoloOnly(pathname)) {
    return <Navigate to="/polo" replace />;
  }
  return children;
};

const router = createBrowserRouter([
  {
    path: "/",
    errorElement: <ErrorElement />,
    element: (
      <ChunkErrorBoundary>
        <RequireAuth>
          <RequirePoloOnlyScope>
            {/*
              MainPage is lazy. A 401 on /calendars is a sync React Query
              update; if this layout re-suspends with no boundary, React 18
              throws #426 ("A component suspended while responding to
              synchronous input"). Child routes already have their own
              Suspense — this one stays mounted across them.
            */}
            <Suspense fallback={<Loading />}>
              <MainPage />
            </Suspense>
          </RequirePoloOnlyScope>
        </RequireAuth>
      </ChunkErrorBoundary>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="/calendars" replace />,
      },
      {
        path: "calendars",
        element: (
          <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <CalenderPage />
            </Suspense>
          </ChunkErrorBoundary>
        ),
      },
      { path: "logout", element: <Logout /> },
      {
        path: "checkin",
        element: (
          <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <CheckIn />
            </Suspense>
          </ChunkErrorBoundary>
        ),
      },
      {
        path: "payments",
        element: (
          <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <Payments />
            </Suspense>
          </ChunkErrorBoundary>
        ),
      },
      {
        // Information Intelligence — Feedback, Coach Feedback, Service
        // Ratings and Member Polls share one sidebar item and a tab bar.
        // Each tab keeps its own route so old links still work.
        path: "information-intelligence",
        element: <Navigate to="/feedback" replace />,
      },
      {
        element: (
          <ChunkErrorBoundary>
            <IntelligenceLayout />
          </ChunkErrorBoundary>
        ),
        children: [
          {
            path: "coach-feedback",
            element: (
              <ChunkErrorBoundary>
                <Suspense fallback={<Loading />}>
                  <CoachFeedback />
                </Suspense>
              </ChunkErrorBoundary>
            ),
          },
          {
            // In-app feedback / suggestion box — free text sent from the app,
            // separate from the post-session coach rating above.
            path: "feedback",
            element: (
              <ChunkErrorBoundary>
                <Suspense fallback={<Loading />}>
                  <AppFeedback />
                </Suspense>
              </ChunkErrorBoundary>
            ),
          },
          {
            // Post-service star ratings (GZ#3560) — how members rated the service
            // they used. Distinct from coach-feedback (rates the coach) and
            // feedback (unprompted free text about the app).
            path: "service-feedback",
            element: (
              <ChunkErrorBoundary>
                <Suspense fallback={<Loading />}>
                  <ServiceFeedback />
                </Suspense>
              </ChunkErrorBoundary>
            ),
          },
          {
            // Member Polls (GZ#3561) — the club ASKS members a question, on a
            // cadence, targeted by membership plan. The other three feedback
            // screens report what members said unprompted or after a session;
            // this is the only one that writes.
            path: "polls",
            element: (
              <ChunkErrorBoundary>
                <Suspense fallback={<Loading />}>
                  <Polls />
                </Suspense>
              </ChunkErrorBoundary>
            ),
          },
        ],
      },
      {
        // Screens & Live Score — TVs at the venue (loop of scenes, court
        // assignment, pairing) and live matches staff score from a phone.
        path: "screens",
        element: (
          <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <Screens />
            </Suspense>
          </ChunkErrorBoundary>
        ),
      },
      {
        path: "screens/:screenId",
        element: (
          <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <ScreenEditor />
            </Suspense>
          </ChunkErrorBoundary>
        ),
      },
      {
        // Forms (admin) — the list, then the builder (Build / Settings /
        // Sign-ups) for one form. The public pages live outside this tree.
        path: "forms",
        element: (
          <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <Forms />
            </Suspense>
          </ChunkErrorBoundary>
        ),
      },
      {
        // "edit" is a static segment, so it outranks the public
        // /forms/:clubSlug/:formSlug route below.
        path: "forms/edit/:formId",
        element: (
          <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <FormEditor />
            </Suspense>
          </ChunkErrorBoundary>
        ),
      },
      {
        // Full-page Bundle Builder (replaces the old bundle modal on the
        // Payments → Memberships → Bundles tab).
        path: "memberships/bundles/new",
        element: (
          <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <BundleBuilderPage />
            </Suspense>
          </ChunkErrorBoundary>
        ),
      },
      {
        path: "memberships/bundles/:id/edit",
        element: (
          <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <BundleBuilderPage />
            </Suspense>
          </ChunkErrorBoundary>
        ),
      },
      {
        path: "courts",
        element: (
          <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <CourtsSetting />
            </Suspense>
          </ChunkErrorBoundary>
        ),
      },
      {
        path: "customers",
        element: (
          <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <Customers />
            </Suspense>
          </ChunkErrorBoundary>
        ),
      },
      {
        path: "verifications",
        element: (
          <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <Verifications />
            </Suspense>
          </ChunkErrorBoundary>
        ),
      },
      {
        // Expiring Soon moved into Payments → Memberships (and Club Credits).
        // Keep old links and bookmarks working.
        path: "expiring",
        element: <Navigate to="/payments?tab=memberships&view=expiring" replace />,
      },
      {
        // Tournaments render inside the normal admin layout (sidebar). The
        // shell self-routes list vs /tournaments/:id off the URL.
        path: "tournaments/*",
        element: (
          <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <TournamentsApp />
            </Suspense>
          </ChunkErrorBoundary>
        ),
      },
      {
        path: "trips",
        element: (
          <RequireTripsClub>
            <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <Trips />
            </Suspense>
          </ChunkErrorBoundary>
          </RequireTripsClub>
        ),
      },
      {
        path: "trips/new",
        element: (
          <RequireTripsClub>
            <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <TripCreate />
            </Suspense>
          </ChunkErrorBoundary>
          </RequireTripsClub>
        ),
      },
      {
        path: "trips/site",
        element: (
          <RequireTripsClub>
            <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <TripSite />
            </Suspense>
          </ChunkErrorBoundary>
          </RequireTripsClub>
        ),
      },
      {
        path: "trips/team",
        element: (
          <RequireTripsClub>
            <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <TripTeam />
            </Suspense>
          </ChunkErrorBoundary>
          </RequireTripsClub>
        ),
      },
      {
        path: "trips/customers",
        element: (
          <RequireTripsClub>
            <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <TripCustomers />
            </Suspense>
          </ChunkErrorBoundary>
          </RequireTripsClub>
        ),
      },
      {
        path: "trips/documents",
        element: (
          <RequireTripsClub>
            <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <TripDocuments />
            </Suspense>
          </ChunkErrorBoundary>
          </RequireTripsClub>
        ),
      },
      {
        path: "trips/:id/edit",
        element: (
          <RequireTripsClub>
            <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <TripCreate />
            </Suspense>
          </ChunkErrorBoundary>
          </RequireTripsClub>
        ),
      },
      {
        path: "trips/:id",
        element: (
          <RequireTripsClub>
            <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <TripDetail />
            </Suspense>
          </ChunkErrorBoundary>
          </RequireTripsClub>
        ),
      },
      {
        // Restaurant module (Tavola) — one route catches every view
        // (/restaurant, /restaurant/checks, /restaurant/kitchen/grill, …);
        // TavolaShell reads useLocation() (tavola/router.ts) and renders
        // full-screen. Non-enabled clubs see its "Coming soon" screen.
        path: "restaurant/*",
        element: (
          <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <Restaurant />
            </Suspense>
          </ChunkErrorBoundary>
        ),
      },
      {
        // Polo module — one route catches every section
        // (/polo, /polo/practices, /polo/practices/:id, /polo/tournaments, …);
        // PoloShell reads useLocation() to pick the active screen. Gated to
        // polo clubs — RequirePoloClub bounces others to /calendars.
        path: "polo/*",
        element: (
          <RequirePoloClub>
            <ChunkErrorBoundary>
              <Suspense fallback={<Loading />}>
                <PoloLayout />
              </Suspense>
            </ChunkErrorBoundary>
          </RequirePoloClub>
        ),
      },
      {
        // Lodging module — one route catches every section
        // (/lodging, /lodging/bookings, /lodging/bookings/:id, /lodging/chart,
        // …); LodgingShell reads useLocation() to pick the active screen.
        // Gated to lodging clubs — RequireLodgingClub bounces others to
        // /calendars.
        path: "lodging/*",
        element: (
          <RequireLodgingClub>
            <ChunkErrorBoundary>
              <Suspense fallback={<Loading />}>
                <LodgingLayout />
              </Suspense>
            </ChunkErrorBoundary>
          </RequireLodgingClub>
        ),
      },
      {
        path: "super-accounts",
        element: (
          <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <SuperAccounts />
            </Suspense>
          </ChunkErrorBoundary>
        ),
      },
      {
        path: "services",
        element: (
          <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <Services />
            </Suspense>
          </ChunkErrorBoundary>
        ),
      },
      {
        path: "stats",
        element: (
          <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <Statistics />
            </Suspense>
          </ChunkErrorBoundary>
        ),
      },
      {
        path: "mcp-connection",
        element: <RequireClubAdmin><ChunkErrorBoundary><Suspense fallback={<Loading />}><McpConnectionPage /></Suspense></ChunkErrorBoundary></RequireClubAdmin>,
      },
      {
        path: "stats/ai-assistant",
        element: (
          <RequireClubAdmin>
            <ChunkErrorBoundary>
              <Suspense fallback={<Loading />}>
                <StatsAssistantPage />
              </Suspense>
            </ChunkErrorBoundary>
          </RequireClubAdmin>
        ),
      },
      {
        path: "app-analytics",
        element: (
          <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <AppAnalyticsPage />
            </Suspense>
          </ChunkErrorBoundary>
        ),
      },
      {
        path: "accounting",
        element: (
          <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <Accounting />
            </Suspense>
          </ChunkErrorBoundary>
        ),
      },
      {
        path: "POS",
        element: (
          <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <POS />
            </Suspense>
          </ChunkErrorBoundary>
        ),
      },
      {
        // Programs (GZ#4541) — term courses: All programs + Students. On for
        // every club (GZ#4774, programsAccess.ts). `:tab` picks the subnav tab.
        path: "programs",
        element: (
          <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <Programs />
            </Suspense>
          </ChunkErrorBoundary>
        ),
      },
      {
        path: "programs/:tab",
        element: (
          <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <Programs />
            </Suspense>
          </ChunkErrorBoundary>
        ),
      },
      {
        // Club footage library (GZ#3608) — recordings the club scheduled on its
        // own courts, and the form to schedule more. Camera clubs only (sidebar
        // gate); the page itself explains when a club has no cameras.
        path: "recordings",
        element: (
          <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <Recordings />
            </Suspense>
          </ChunkErrorBoundary>
        ),
      },
      {
        path: "automations",
        element: (
          <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <Automations />
            </Suspense>
          </ChunkErrorBoundary>
        ),
      },
      {
        path: "push-notifications",
        element: (
          <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <PushNotifications />
            </Suspense>
          </ChunkErrorBoundary>
        ),
      },
      {
        // Referrals growth console (GZ#1280) — ClubAdmin-level marketing page.
        path: "referrals",
        element: (
          <RequireClubAdmin>
            <ChunkErrorBoundary>
              <Suspense fallback={<Loading />}>
                <Referrals />
              </Suspense>
            </ChunkErrorBoundary>
          </RequireClubAdmin>
        ),
      },
      {
        path: "referrals/:programId",
        element: (
          <RequireClubAdmin>
            <ChunkErrorBoundary>
              <Suspense fallback={<Loading />}>
                <ReferralProgramDetail />
              </Suspense>
            </ChunkErrorBoundary>
          </RequireClubAdmin>
        ),
      },
      {
        // Loyalty console (KROSS Rewards) — ClubAdmin-level growth page.
        path: "loyalty",
        element: (
          <RequireClubAdmin>
            <ChunkErrorBoundary>
              <Suspense fallback={<Loading />}>
                <Loyalty />
              </Suspense>
            </ChunkErrorBoundary>
          </RequireClubAdmin>
        ),
      },
      {
        path: "unpaid-balance-reminders",
        element: (
          <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <UnpaidBalanceReminders />
            </Suspense>
          </ChunkErrorBoundary>
        ),
      },
      {
        path: "customer-messages",
        element: (
          <RequireCustomerMessagesClub>
            <ChunkErrorBoundary>
              <Suspense fallback={<Loading />}>
                <CustomerMessages />
              </Suspense>
            </ChunkErrorBoundary>
          </RequireCustomerMessagesClub>
        ),
      },
      {
        path: "support",
        element: (
          <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <Support />
            </Suspense>
          </ChunkErrorBoundary>
        ),
      },
      {
        path: "agent",
        element: (
          <RequireClubAdmin>
            <ChunkErrorBoundary>
              <Suspense fallback={<Loading />}>
                <BookingAgentPage />
              </Suspense>
            </ChunkErrorBoundary>
          </RequireClubAdmin>
        ),
      },
      {
        path: "changelog",
        element: (
          <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <Changelog />
            </Suspense>
          </ChunkErrorBoundary>
        ),
      },
      {
        /*
          Where Intuit's consent redirect lands (GZ#2787). The backend callback
          at /api/v1/webhooks/quickbooks/callback verifies the signed state,
          exchanges the code and stores the grant, then sends the browser here
          with ?status=connected|error. Without this route the whole OAuth flow
          ends on a 404 and the admin cannot tell whether it worked.

          Inside the authenticated layout on purpose: the admin who started the
          flow is already signed in, and a stray hit should go to sign-in.
        */
        path: "settings/integrations/quickbooks",
        element: (
          <ChunkErrorBoundary>
            <Suspense fallback={<Loading />}>
              <QuickBooksCallback />
            </Suspense>
          </ChunkErrorBoundary>
        ),
      },
      {
        path: "Settings",
        element: (
          <RequireAdminRole>
            <ChunkErrorBoundary>
              <Suspense fallback={<Loading />}>
                <SuperAdminSettings />
              </Suspense>
            </ChunkErrorBoundary>
          </RequireAdminRole>
        ),
      },
    ],
  },
  {
    path: "/signin",
    element: <SignIn />,
  },
  {
    // GZ#4917 — public page for the emailed one-time link
    // (admin.bookandgo.app/reset-password?token=…). Same look as sign-in.
    path: "/reset-password",
    element: <SignIn />,
  },
  {
    path: "/privacy",
    element: <PrivacyPage />,
  },
  {
    path: "/terms",
    element: <TermsPage />,
  },
  {
    path: "/partner/login",
    element: partnerPage(<PartnerLogin />),
  },
  {
    path: "/partner/payment/return",
    element: partnerPage(<PartnerPaymentReturn />),
  },
  {
    // The portal shell routes its own screens off the URL — one element for
    // /partner and every /partner/<screen> path.
    path: "/partner/*",
    element: partnerPage(<PartnerLayout />),
  },
  {
    // Forms — public pages at admin.bookandgo.app/forms/... . No RequireAuth
    // and a bare HTTP client (src/api/FormsPublic.ts), so a logged-out visitor
    // never meets the admin's 401 -> /signin redirect. Static segments win in
    // React Router's ranking: /forms/r/:ref and the admin /forms/edit/:formId
    // beat /forms/:clubSlug/:formSlug; the admin list stays /forms.
    path: "/forms/r/:ref",
    element: (
      <ChunkErrorBoundary>
        <Suspense fallback={<Loading />}>
          <PublicResultPage />
        </Suspense>
      </ChunkErrorBoundary>
    ),
  },
  {
    path: "/forms/:clubSlug/:formSlug",
    element: (
      <ChunkErrorBoundary>
        <Suspense fallback={<Loading />}>
          <PublicFormPage />
        </Suspense>
      </ChunkErrorBoundary>
    ),
  },
  {
    path: "/forms/:clubSlug",
    element: (
      <ChunkErrorBoundary>
        <Suspense fallback={<Loading />}>
          <PublicClubPage />
        </Suspense>
      </ChunkErrorBoundary>
    ),
  },
  {
    // GZ#3749 — membership purchase link. Declared BEFORE the two-segment
    // booking route below so "membership" is never read as a booking id.
    path: "/pay/membership/:linkId",
    element: (
      <ChunkErrorBoundary>
        <Suspense fallback={<Loading />}>
          <PublicMembershipPayment />
        </Suspense>
      </ChunkErrorBoundary>
    ),
  },
  {
    // Gift credit link (PISTA). Before the two-segment booking route too.
    path: "/pay/voucher/:linkId",
    element: (
      <ChunkErrorBoundary>
        <Suspense fallback={<Loading />}>
          <PublicVoucherPayment />
        </Suspense>
      </ChunkErrorBoundary>
    ),
  },
  {
    path: "/pay/:serviceBookingId/:playerId",
    element: (
      <ChunkErrorBoundary>
        <Suspense fallback={<Loading />}>
          <PublicPayment />
        </Suspense>
      </ChunkErrorBoundary>
    ),
  },
  {
    // GZ#3060 — a community group's invite link. Public by design, exactly
    // like /kiosk/:token above: the token is high-entropy, revocable and
    // capped by the booking's own capacity, and the invitee is not a Book and
    // Go user yet — a login wall here loses them.
    path: "/join/:clubId/:token",
    element: (
      <ChunkErrorBoundary>
        <Suspense fallback={<Loading />}>
          <CommunityInviteJoin />
        </Suspense>
      </ChunkErrorBoundary>
    ),
  },
  {
    // GZ#3693 — guest sign-up form. Public by design, like /join above: the
    // guest has no account yet, and the page only creates one (no token back).
    // ?location=<id> picks that location's waiver.
    path: "/guest/:clubId",
    element: (
      <ChunkErrorBoundary>
        <Suspense fallback={<Loading />}>
          <GuestSignup />
        </Suspense>
      </ChunkErrorBoundary>
    ),
  },
  {
    path: "/chat/:clubId",
    element: (
      <ChunkErrorBoundary>
        <Suspense fallback={<Loading />}>
          <PublicChatPage />
        </Suspense>
      </ChunkErrorBoundary>
    ),
  },
  {
    // Screens & Live Score public surfaces. Order matters: "desk" and "remote"
    // must be matched before "/screens/:token" or they read as screen tokens.
    // The desk is staff-only (RequireAuth) but has no admin chrome — it is a
    // phone page. The TV and the remote hold no session: the token in the URL
    // is the whole access model, scoped server-side to one screen / one match.
    path: "/screens/desk",
    element: (
      <ChunkErrorBoundary>
        <RequireAuth>
          <Suspense fallback={<Loading />}>
            <CourtDesk />
          </Suspense>
        </RequireAuth>
      </ChunkErrorBoundary>
    ),
  },
  {
    path: "/screens/remote/:controlToken",
    element: (
      <ChunkErrorBoundary>
        <Suspense fallback={<Loading />}>
          <ScreenRemote />
        </Suspense>
      </ChunkErrorBoundary>
    ),
  },
  {
    // A court's YouTube stream: OBS loads this over the camera. See-through,
    // score only. Before "/screens/:screenToken" so "overlay" is not a token.
    path: "/screens/overlay/:screenToken",
    element: (
      <ChunkErrorBoundary>
        <Suspense fallback={null}>
          <ScreenOverlay />
        </Suspense>
      </ChunkErrorBoundary>
    ),
  },
  {
    path: "/screens/:screenToken",
    element: (
      <ChunkErrorBoundary>
        <Suspense fallback={<Loading />}>
          <ScreenTV />
        </Suspense>
      </ChunkErrorBoundary>
    ),
  },
  {
    // Americano / Mexicano club screen. Public by design: the slug is random
    // (am-<16 hex>) and the view has no customer ids; the court-QR score page
    // also needs the key in its link (?k=), checked server-side.
    path: "/live/:slug",
    element: (
      <ChunkErrorBoundary>
        <Suspense fallback={<Loading />}>
          <AmericanoScreen />
        </Suspense>
      </ChunkErrorBoundary>
    ),
  },
  {
    path: "/live/:slug/score/:matchId",
    element: (
      <ChunkErrorBoundary>
        <Suspense fallback={<Loading />}>
          <AmericanoScore />
        </Suspense>
      </ChunkErrorBoundary>
    ),
  },
  {
    // Self-serve check-in kiosk (GZ#2469 round 4). Public by design — the token
    // is a per-location high-entropy slug minted in Settings; the backend scopes
    // everything to that location and never trusts the device.
    path: "/kiosk/:token",
    element: (
      <ChunkErrorBoundary>
        <Suspense fallback={<Loading />}>
          <Kiosk />
        </Suspense>
      </ChunkErrorBoundary>
    ),
  },
  {
    path: "/payment/success",
    element: (
      <ChunkErrorBoundary>
        <Suspense fallback={<Loading />}>
          <PaymentSuccess />
        </Suspense>
      </ChunkErrorBoundary>
    ),
  },
  {
    path: "/404",
    element: (
      <ChunkErrorBoundary>
        <Suspense fallback={<Loading />}>
          <ErrorElement />
        </Suspense>
      </ChunkErrorBoundary>
    ),
  },
  // Redirect for any non-existing route — host-aware so a stray URL on the
  // partner domain never lands on the admin calendar.
  {
    path: "*",
    element: (
      <Navigate to={isPartnerHost() ? "/partner" : isPoloOnlyClub() ? "/polo" : "/calendars"} />
    ),
  },
]);

export default router;
